import React, { useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Beaker,
  Box,
  Boxes,
  Briefcase,
  Building2,
  Compass,
  Factory,
  FileText,
  FlaskConical,
  FolderKanban,
  Globe,
  Handshake,
  Landmark,
  Lock,
  Megaphone,
  Microscope,
  Package,
  PartyPopper,
  PieChart,
  RotateCcw,
  Search,
  Share2,
  ShieldAlert,
  Sparkles,
  Star,
  Store,
  TrendingUp,
  Truck,
  UserPlus,
  Users,
  Warehouse,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import LegalDisclaimer, { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import DocumentCard from "@/components/agreements/DocumentCard";
import {
  FINDER_INTENTS,
  FINDER_BY_ID,
  FINDER_GROUPS,
  finderResult,
} from "@/data/agreements/finderQuestions";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { eventsApi } from "@/lib/agreements/api";

/** Icon names used by finderQuestions.js, resolved to components. */
const ICONS = {
  BadgeCheck,
  Beaker,
  Box,
  Boxes,
  Briefcase,
  Building2,
  Factory,
  FlaskConical,
  Globe,
  Handshake,
  Landmark,
  Lock,
  Megaphone,
  Microscope,
  Package,
  PartyPopper,
  PieChart,
  Share2,
  ShieldAlert,
  Sparkles,
  Star,
  Store,
  TrendingUp,
  Truck,
  UserPlus,
  Users,
  Warehouse,
  Wrench,
};

/** Escape hatch on a follow-up question: show everything the intent covers. */
const ANY = "any";

/**
 * AgreementFinder — route: /agreements/finder
 *
 * One question, sometimes two. For the founder who has one thing in front of them right now
 * and does not want to fill in a whole intake to find out what it needs.
 *
 * The chosen intent and follow-up answer live in the query string (?intent=&answer=) so a
 * result set can be sent to a co-founder or an attorney as a link.
 */
export default function AgreementFinder() {
  const [params, setParams] = useSearchParams();

  const rawIntent = params.get("intent") || "";
  const rawAnswer = params.get("answer") || "";
  const intent = FINDER_BY_ID[rawIntent] || null;
  const unknownIntent = Boolean(rawIntent) && !intent;

  // Only honour an answer the chosen intent actually offers.
  const answer = useMemo(() => {
    if (!intent?.followUp) return "";
    if (rawAnswer === ANY) return ANY;
    return (intent.followUp.options || []).some((o) => o.value === rawAnswer) ? rawAnswer : "";
  }, [intent, rawAnswer]);

  const needsFollowUp = Boolean(intent?.followUp) && !answer;
  const showResults = Boolean(intent) && !needsFollowUp;

  const docs = useMemo(() => {
    if (!intent) return [];
    const { docIds } = finderResult(intent.id, answer === ANY ? "" : answer);
    return (docIds || []).map((id) => DOC_BY_ID[id]).filter(Boolean);
  }, [intent, answer]);

  // One run_finder event per distinct result set the founder actually reaches.
  const tracked = useRef(null);
  useEffect(() => {
    if (!showResults || !intent) return;
    const key = answer ? `${intent.id}:${answer}` : intent.id;
    if (tracked.current === key) return;
    tracked.current = key;
    eventsApi.track({ type: "run_finder", context: key.slice(0, 120) });
  }, [showResults, intent, answer]);

  const step = !intent ? 1 : needsFollowUp ? 2 : 3;
  const totalSteps = intent?.followUp ? 3 : 2;

  return (
    <AgreementsShell
      crumbs={[
        { label: "Find a document", ...(intent ? { to: "/agreements/finder" } : {}) },
        ...(intent ? [{ label: intent.label }] : []),
      ]}
      title={intent ? intent.label : "What agreement do I need?"}
      subtitle={
        !intent
          ? "Pick the one thing you are about to do. We will show you the short list that applies to it — not your whole document plan."
          : needsFollowUp
            ? "One more question and we can narrow this down."
            : intent.lead
      }
      actions={
        intent ? (
          <Button variant="outline" onClick={() => setParams({})}>
            <RotateCcw className="mr-2 h-4 w-4" /> Start over
          </Button>
        ) : undefined
      }
    >
      <Stepper step={step} totalSteps={totalSteps} />

      {unknownIntent && (
        <p className="mb-5 rounded-md border border-pink-200 bg-pink-50 px-3 py-2 text-sm text-pink-900">
          We do not have a step called "{rawIntent}". Pick one from the list below.
        </p>
      )}

      {/* ── Step 1 · what are you doing? ─────────────────────────────────── */}
      {!intent && (
        <>
          {FINDER_GROUPS.map((group) => {
            const intents = FINDER_INTENTS.filter((i) => i.group === group);
            if (intents.length === 0) return null;
            return (
              <section key={group} className="mb-8">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {group}
                </h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {intents.map((i) => {
                    const Icon = ICONS[i.icon] || FileText;
                    return (
                      <Link
                        key={i.id}
                        to={`/agreements/finder?intent=${encodeURIComponent(i.id)}`}
                        className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Card className="h-full transition-transform group-hover:-translate-y-0.5 group-hover:border-indigo-300">
                          <CardContent className="flex h-full items-start gap-3 p-4">
                            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50">
                              <Icon className="h-5 w-5" />
                            </span>
                            <div className="min-w-0">
                              <h3 className="font-semibold leading-tight">{i.label}</h3>
                              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                {i.lead}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <div className="rounded-lg border border-dashed p-5 text-center">
            <p className="text-sm text-muted-foreground">
              Not on the list, or you want the whole picture rather than one answer?
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Button asChild>
                <Link to="/agreements/intake">
                  <FolderKanban className="mr-2 h-4 w-4" /> Build my full plan
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/agreements/library">
                  <Search className="mr-2 h-4 w-4" /> Browse the whole library
                </Link>
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ── Step 2 · the follow-up ───────────────────────────────────────── */}
      {intent && needsFollowUp && (
        <section className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">{intent.followUp.question}</h2>
          <div className="flex flex-wrap gap-2">
            {(intent.followUp.options || []).map((opt) => (
              <Button
                key={opt.value}
                asChild
                size="lg"
                variant="outline"
                className="h-auto rounded-full px-5 py-2.5 text-base hover:border-indigo-300 hover:bg-indigo-50"
              >
                <Link
                  to={`/agreements/finder?intent=${encodeURIComponent(intent.id)}&answer=${encodeURIComponent(opt.value)}`}
                >
                  {opt.label}
                </Link>
              </Button>
            ))}
            <Button asChild size="lg" variant="ghost" className="h-auto rounded-full px-5 py-2.5">
              <Link to={`/agreements/finder?intent=${encodeURIComponent(intent.id)}&answer=${ANY}`}>
                Show me all of them
              </Link>
            </Button>
          </div>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{intent.lead}</p>
        </section>
      )}

      {/* ── Step 3 · results ─────────────────────────────────────────────── */}
      {showResults && (
        <>
          {intent.attorneyLed && (
            <AttorneyBadgeNote
              level="strongly-recommended"
              note="These are documents to prepare for and bring to counsel. The entries below exist so you arrive informed — not so you can paper this yourself."
              className="mb-5"
            />
          )}

          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {docs.length > 0
                ? `${docs.length} ${docs.length === 1 ? "document" : "documents"} for this`
                : "Nothing matched"}
            </h2>
            {intent.followUp && (
              <Button asChild variant="ghost" size="sm">
                <Link to={`/agreements/finder?intent=${encodeURIComponent(intent.id)}`}>
                  Change my answer
                </Link>
              </Button>
            )}
          </div>

          {docs.length > 0 ? (
            <div className="mb-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {docs.map((doc) => (
                <DocumentCard key={doc.id} doc={doc} />
              ))}
            </div>
          ) : (
            <p className="mb-8 rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              We could not resolve documents for this step. Browse the library or run the full
              intake instead.
            </p>
          )}

          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            <CtaCard
              to="/agreements/intake"
              icon={FolderKanban}
              title="See my full plan"
              blurb="This is one moment in the business. Answer the intake once and get every document your brand actually needs, in the order you meet them."
              primary
            />
            <CtaCard
              to="/agreements/library"
              icon={Search}
              title="Browse the whole library"
              blurb="Every agreement a CPG brand runs into, searchable and filterable by the part of the business you are working on."
            />
          </div>

          <div className="mb-6">
            <Button variant="outline" onClick={() => setParams({})}>
              <Compass className="mr-2 h-4 w-4" /> Ask a different question
            </Button>
          </div>
        </>
      )}

      <LegalDisclaimer variant="library" />
    </AgreementsShell>
  );
}

function Stepper({ step, totalSteps }) {
  const labels = totalSteps === 3
    ? ["What are you doing?", "One more question", "Your documents"]
    : ["What are you doing?", "Your documents"];
  return (
    <ol className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label} className="flex items-center gap-2">
            {i > 0 && <span className="text-muted-foreground/50">→</span>}
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
                active && "bg-indigo-600 font-medium text-white",
                done && "bg-muted text-foreground",
              )}
            >
              <span className="tabular-nums">{n}</span> {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function CtaCard({ to, icon: Icon, title, blurb, primary }) {
  return (
    <Link to={to} className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Card
        className={cn(
          "h-full transition-transform group-hover:-translate-y-0.5",
          primary
            ? "border-indigo-300 bg-indigo-50/70 dark:border-indigo-900 dark:bg-indigo-950/30"
            : "border-dashed",
        )}
      >
        <CardContent className="flex h-full items-start gap-3 p-4">
          <span
            className={cn(
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              primary ? "bg-indigo-600 text-white" : "bg-muted text-foreground",
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold leading-tight">{title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium">
              {title}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
